/**
 * Work Queue: a generic, user-facing "needs your attention" surface (NOT admin-only, NOT a notification
 * inbox). Each item type owns its own authorization rules on the backend; the page renders a single
 * permission-filtered list. New types plug in via backend resolvers, no new tabs, no schema changes.
 */

export enum WorkQueueItemType {
  BudgetReview = "BUDGET_REVIEW",
  MemberConfirmation = "MEMBER_CONFIRMATION",
  TransferApproval = "TRANSFER_APPROVAL",
  ReconciliationReview = "RECONCILIATION_REVIEW",
}

export enum WorkQueueEntityType {
  Budget = "BUDGET",
  Member = "MEMBER",
  FinanceTransfer = "FINANCE_TRANSFER",
  ReconciliationSession = "RECONCILIATION_SESSION",
}

export enum WorkQueueScopeKind {
  Branch = "BRANCH",
  Parish = "PARISH",
  Workspace = "WORKSPACE",
  User = "USER",
}

/** The item statuses resolvers send (lower-cased source statuses), for their labels and tones. */
export enum WorkQueueItemStatus {
  PendingReview = "pending_review",
  ReadyForReview = "ready_for_review",
  InReview = "in_review",
  /** A member registration waiting to be confirmed. */
  PendingConfirmation = "pending_confirmation",
  /** A transfer waiting for the receiving side. */
  Pending = "pending",
}

/** The desktop list's groups: items nobody has picked up yet, then items already being reviewed. */
export enum WorkQueueStage {
  ToPickUp = "to_pick_up",
  InReview = "in_review",
}

export enum WorkQueuePriority {
  Normal = "NORMAL",
  High = "HIGH",
}

export type WorkQueueItem = {
  id: string
  type: WorkQueueItemType
  categoryLabelKey: string
  entityType: WorkQueueEntityType
  entityId: string
  title: string
  description: string | null
  status: string
  priority: WorkQueuePriority
  scopeKind: WorkQueueScopeKind
  workspaceId: string
  branchId: string | null
  assignedUserId: string | null
  submittedByUserId: string | null
  submittedByName: string | null
  submittedByAvatarUrl: string | null
  submittedAt: string | null
  /** Relative SPA path to the source entity (e.g. `/budgets/{id}`); the client composes the full URL. */
  actionUrl: string
  metadata: Record<string, unknown>
}

export type WorkQueueGroupSummary = {
  type: WorkQueueItemType
  count: number
  /** When the longest-waiting item of this type was submitted (ISO), or null when the group is empty. */
  oldestSubmittedAt: string | null
}

export type WorkQueueSummary = {
  totalCount: number
  groups: WorkQueueGroupSummary[]
}

/** One page of the permission-filtered queue. `total` and `totalPages` cover every page, not just this one. */
export type WorkQueueItemsPage = {
  items: WorkQueueItem[]
  total: number
  /** The page the server returned: a page past the end comes back as the last one. */
  page: number
  limit: number
  totalPages: number
}
